import {
  MaterialLineItem,
  ModifierApplication,
  CalculationResult,
  Material,
  Process
} from '../types';

/**
 * Calculates markup factor from percentage
 * 100% markup = 2.0 (doubles the price)
 * 50% markup = 1.5
 * 200% markup = 3.0
 */
export const markupPercentageToFactor = (percentage: number): number => {
  return 1 + (percentage / 100);
};

/**
 * Calculates markup percentage from factor
 * 2.0 factor = 100% markup
 * 1.5 factor = 50% markup
 */
export const markupFactorToPercentage = (factor: number): number => {
  return (factor - 1) * 100;
};

/**
 * Calculates total process costs for a material
 * Processes are added at cost without markup applied
 */
export const calculateProcessCosts = (modifiers: ModifierApplication[]): number => {
  return modifiers
    .filter(m => m.isSelected)
    .reduce((sum, modifier) => sum + modifier.costValue, 0);
};

/**
 * Main calculation for material line items
 * Formula:
 * Material After Markup = Base Price × Markup Factor
 * Total Process Costs = Sum of all process costs (no markup)
 * Unit Total = Material After Markup + Total Process Costs
 * Line Total = Unit Total × Quantity
 */
export const calculateMaterialLineItem = (
  material: Material,
  quantity: number,
  modifiers: ModifierApplication[]
): CalculationResult => {
  // Material calculations
  const materialCostBeforeMarkup = material.basePrice;
  const materialCostAfterMarkup = material.basePrice * material.markupFactor;

  // Process costs (no markup applied)
  const processCosts = modifiers
    .filter(m => m.isSelected)
    .map(m => m.costValue);
  const totalProcessCosts = processCosts.reduce((sum, cost) => sum + cost, 0);

  // Unit totals
  const unitCostBeforeMarkup = materialCostBeforeMarkup + totalProcessCosts;
  const unitCostAfterMarkup = materialCostAfterMarkup + totalProcessCosts;

  // Line totals (multiply by quantity)
  const totalCostBeforeMarkup = unitCostBeforeMarkup * quantity;
  const totalCostAfterMarkup = unitCostAfterMarkup * quantity;

  return {
    unitCostBeforeMarkup,
    unitCostAfterMarkup,
    totalCostBeforeMarkup,
    totalCostAfterMarkup,
    breakdown: {
      materialCost: materialCostBeforeMarkup,
      materialAfterMarkup: materialCostAfterMarkup,
      processCosts,
      finalUnitCost: unitCostAfterMarkup,
    },
  };
};

/**
 * Calculates labor costs
 * Formula:
 * Quantity can be hours or sqft depending on rateType
 * Total Before Markup = Quantity × Rate
 * Total After Markup = Total Before Markup × Markup Factor
 */
export const calculateLaborCost = (
  quantity: number,
  rate: number,
  markupFactor: number
): { totalBefore: number; totalAfter: number } => {
  const totalBefore = quantity * rate;
  const totalAfter = totalBefore * markupFactor;

  return {
    totalBefore,
    totalAfter,
  };
};

/**
 * Calculates vinyl/printing costs
 * Formula:
 * Total Before Markup = Sqft × Price Per Sqft + Process Costs
 * Total After Markup = (Sqft × Price Per Sqft × Markup Factor) + Process Costs
 */
export const calculateVinylCost = (
  sqftNeeded: number,
  pricePerSqft: number,
  markupFactor: number,
  processCosts: number = 0
): { totalBefore: number; totalAfter: number } => {
  const vinylCostBefore = sqftNeeded * pricePerSqft;
  const vinylCostAfter = vinylCostBefore * markupFactor;

  return {
    totalBefore: vinylCostBefore + processCosts,
    totalAfter: vinylCostAfter + processCosts,
  };
};

/**
 * Calculates printing costs
 * Same as vinyl - priced per sqft with optional markup
 */
export const calculatePrintingCost = (
  sqftToPrint: number,
  pricePerSqft: number,
  markupFactor: number,
  processCosts: number = 0
): { totalBefore: number; totalAfter: number } => {
  return calculateVinylCost(sqftToPrint, pricePerSqft, markupFactor, processCosts);
};

/**
 * Calculates project-level totals
 */
export const calculateProjectTotals = (
  allLineItemsAfterMarkup: number,
  projectDiscount: number = 0,
  shippingCost: number = 0,
  taxRate: number = 0
): {
  subtotal: number;
  discountAmount: number;
  subtotalAfterDiscount: number;
  shippingCost: number;
  subtotalBeforeTax: number;
  taxAmount: number;
  finalTotal: number;
} => {
  const subtotal = allLineItemsAfterMarkup;
  const discountAmount = subtotal * (projectDiscount / 100);
  const subtotalAfterDiscount = subtotal - discountAmount;
  const subtotalBeforeTax = subtotalAfterDiscount + shippingCost;
  const taxAmount = subtotalBeforeTax * (taxRate / 100);
  const finalTotal = subtotalBeforeTax + taxAmount;

  return {
    subtotal,
    discountAmount,
    subtotalAfterDiscount,
    shippingCost,
    subtotalBeforeTax,
    taxAmount,
    finalTotal,
  };
};

/**
 * Formats currency for display
 */
export const formatCurrency = (amount: number, symbol: string = '$'): string => {
  return `${symbol}${amount.toFixed(2)}`;
};
